import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';

import { GetIntercomMessengerAuthDocument } from 'graphql-query-contracts';

/** Refetch JWT this many ms before `intercomUserJwtExpiresAt` (Unix seconds from the API). */
export const INTERCOM_JWT_REFRESH_LEAD_MS = 5 * 60 * 1000;

/** How often to compare wall clock to JWT expiry while Intercom auth is active (avoids one long deferred timer). */
export const INTERCOM_JWT_STALENESS_CHECK_INTERVAL_MS = 60 * 1000;

/** True when `nowMs` has reached the refresh threshold for the given JWT expiry (Unix seconds). */
export function shouldRefreshIntercomJwt(
  expiresAtSeconds: number,
  nowMs: number,
  leadMs: number
): boolean {
  const expiresAtMs = expiresAtSeconds * 1000;

  return nowMs >= expiresAtMs - leadMs;
}

export type IntercomConfig = {
  appId: string;
  intercomUserJwt: string;
  intercomUserJwtExpiresAt: number;
};

type IntercomMessengerAuthFields = {
  channelId?: string | null;
  appId?: string | null;
  intercomUserJwt?: string | null;
  intercomUserJwtExpiresAt?: number | null;
};

/** Returns validated config when GraphQL auth row matches `channelId`; otherwise null. */
function parseIntercomConfigFromMessengerAuth(
  channelId: string,
  auth: IntercomMessengerAuthFields | null | undefined
): IntercomConfig | null {
  if (!auth || auth.channelId !== channelId) {
    return null;
  }

  const { appId, intercomUserJwt, intercomUserJwtExpiresAt } = auth;

  if (
    typeof appId !== 'string' ||
    !appId ||
    typeof intercomUserJwt !== 'string' ||
    !intercomUserJwt ||
    typeof intercomUserJwtExpiresAt !== 'number'
  ) {
    return null;
  }

  return {
    appId,
    intercomUserJwt,
    intercomUserJwtExpiresAt,
  };
}

type UseIntercomConfigResult = {
  intercomConfig: IntercomConfig | null;
  channelIntegrationsLoaded: boolean;
};

export function useIntercomConfig(
  channelId: string | undefined,
  enabled: boolean
): UseIntercomConfigResult {
  const [fetchIntercomMessengerAuth, { data, loading }] = useLazyQuery(
    GetIntercomMessengerAuthDocument,
    {
      fetchPolicy: 'network-only',
    }
  );

  const channelIdRef = useRef<string | undefined>(channelId);
  const enabledRef = useRef(enabled);
  const expiresAtSecondsRef = useRef<number | undefined>(undefined);
  const refreshInFlightRef = useRef(false);
  const executeIntercomAuthFetchRef = useRef<(action: string) => void>(() => {});
  const lastStableIntercomConfigRef = useRef<IntercomConfig | null>(null);
  /** Channel id for `lastStableIntercomConfigRef`; cleared when `channelId` changes. */
  const stableConfigChannelIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    channelIdRef.current = channelId;
  }, [channelId]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !channelId) {
      expiresAtSecondsRef.current = undefined;

      return;
    }

    const auth = data?.intercomMessengerAuth;

    if (
      auth?.channelId === channelId &&
      typeof auth.intercomUserJwtExpiresAt === 'number'
    ) {
      expiresAtSecondsRef.current = auth.intercomUserJwtExpiresAt;

      return;
    }

    expiresAtSecondsRef.current = undefined;
  }, [enabled, channelId, data]);

  const executeIntercomAuthFetch = useCallback(
    (action: string) => {
      const cid = channelIdRef.current;

      if (!cid) {
        return;
      }

      if (refreshInFlightRef.current) {
        return;
      }

      refreshInFlightRef.current = true;
      const startedForChannelId = cid;

      fetchIntercomMessengerAuth({ variables: { channelId: cid } })
        .catch((error) => {
          datadogRum.addError(error, {
            component: 'useIntercomConfig',
            action,
          });
        })
        .finally(() => {
          refreshInFlightRef.current = false;

          const latestChannelId = channelIdRef.current;

          if (
            enabledRef.current &&
            latestChannelId &&
            latestChannelId !== startedForChannelId
          ) {
            queueMicrotask(() => {
              executeIntercomAuthFetchRef.current(
                'fetchIntercomMessengerAuthAfterChannelChangeWhileInFlight'
              );
            });
          }
        });
    },
    [fetchIntercomMessengerAuth]
  );

  executeIntercomAuthFetchRef.current = executeIntercomAuthFetch;

  const tryRefreshIfStale = useCallback(
    (action: string) => {
      const cid = channelIdRef.current;
      const exp = expiresAtSecondsRef.current;

      if (!cid || typeof exp !== 'number') {
        return;
      }

      if (
        !shouldRefreshIntercomJwt(exp, Date.now(), INTERCOM_JWT_REFRESH_LEAD_MS)
      ) {
        return;
      }

      executeIntercomAuthFetch(action);
    },
    [executeIntercomAuthFetch]
  );

  useEffect(() => {
    if (stableConfigChannelIdRef.current !== channelId) {
      lastStableIntercomConfigRef.current = null;
      stableConfigChannelIdRef.current = undefined;
    }

    if (!enabled || !channelId) {
      return;
    }

    executeIntercomAuthFetch('fetchIntercomMessengerAuth');
  }, [channelId, enabled, executeIntercomAuthFetch]);

  useEffect(() => {
    if (!enabled || !channelId) {
      return;
    }

    const parsedIntercomConfig = parseIntercomConfigFromMessengerAuth(
      channelId,
      data?.intercomMessengerAuth
    );

    if (parsedIntercomConfig) {
      lastStableIntercomConfigRef.current = parsedIntercomConfig;
      stableConfigChannelIdRef.current = channelId;

      return;
    }

    if (data?.intercomMessengerAuth?.channelId === channelId) {
      lastStableIntercomConfigRef.current = null;
      stableConfigChannelIdRef.current = undefined;
    }
  }, [channelId, data, enabled]);

  const intercomConfig = useMemo(() => {
    if (!enabled || !channelId) {
      return null;
    }

    const parsedIntercomConfig = parseIntercomConfigFromMessengerAuth(
      channelId,
      data?.intercomMessengerAuth
    );

    if (parsedIntercomConfig) {
      return parsedIntercomConfig;
    }

    if (
      loading &&
      stableConfigChannelIdRef.current === channelId &&
      lastStableIntercomConfigRef.current
    ) {
      return lastStableIntercomConfigRef.current;
    }

    return null;
  }, [channelId, data, enabled, loading]);

  useEffect(() => {
    if (
      !enabled ||
      !channelId ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return;
    }

    const setupIntervalStalenessChecks = () => {
      return window.setInterval(() => {
        tryRefreshIfStale(
          'refreshIntercomMessengerAuthIntervalStalenessCheck'
        );
      }, INTERCOM_JWT_STALENESS_CHECK_INTERVAL_MS);
    };

    const setupDocumentStaleRefreshListeners = () => {
      const runIfStale = (action: string) => {
        tryRefreshIfStale(action);
      };

      const onVisibilityChange = () => {
        if (document.visibilityState !== 'visible') {
          return;
        }

        runIfStale('refreshIntercomMessengerAuthOnDocumentVisible');
      };

      const onWindowFocus = () => {
        runIfStale('refreshIntercomMessengerAuthOnWindowFocus');
      };

      const onPageShow = (event: Event) => {
        if (Reflect.get(event, 'persisted') === true) {
          runIfStale('refreshIntercomMessengerAuthOnPageShowPersisted');
        }
      };

      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('focus', onWindowFocus);
      window.addEventListener('pageshow', onPageShow);

      return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('focus', onWindowFocus);
        window.removeEventListener('pageshow', onPageShow);
      };
    };

    const intervalId = setupIntervalStalenessChecks();
    const removeListeners = setupDocumentStaleRefreshListeners();

    return () => {
      window.clearInterval(intervalId);
      removeListeners();
    };
  }, [channelId, enabled, tryRefreshIfStale]);

  const channelIntegrationsLoaded = Boolean(
    (!enabled || !channelId) ||
      (!loading && data?.intercomMessengerAuth?.channelId === channelId)
  );

  return {
    intercomConfig,
    channelIntegrationsLoaded,
  };
}
